import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Stage, Layer, Rect, Text, Image as KImage, Group, Transformer } from "react-konva";
import useImage from "use-image";
import { fileUrl, API, BACKEND_URL } from "@/lib/api";
import { sketchify, FACE_FILTERS } from "@/lib/sketchify";
import { toast } from "sonner";
import { Upload, ShoppingCart, Palette, Type, Image as ImageIcon, Loader2, Check, Lock, ExternalLink } from "lucide-react";
import axios from "axios";

const CANVAS_SIZE = 1000;

function BaseImage({ url }) {
  const [img] = useImage(url, "anonymous");
  if (!img) return null;
  return <KImage image={img} width={CANVAS_SIZE} height={CANVAS_SIZE} listening={false} />;
}

function ZoneImage({ url, width, height }) {
  const [img] = useImage(url, "anonymous");
  if (!img) return null;
  const iw = img.width, ih = img.height;
  const scale = Math.min(width / iw, height / ih);
  const w = iw * scale, h = ih * scale;
  return <KImage image={img} width={w} height={h} x={(width - w) / 2} y={(height - h) / 2} />;
}

// Editable image inside a zone: honours allow_zoom / allow_move.
// The image's natural aspect ratio is preserved; content is clipped to the zone bounds so it
// never escapes the seller-defined printable area. `d` holds the buyer's transform:
//   d.zoom = 1 → fit-to-zone; higher zooms in.
//   d.offsetX / d.offsetY = drag offsets in canvas px, relative to the fit-centred position.
function EditableZoneImage({ url, width, height, d, canMove, onChange }) {
  const [img] = useImage(url, "anonymous");
  if (!img) return null;
  const iw = img.width, ih = img.height;
  const fitScale = Math.min(width / iw, height / ih);
  const zoom = Math.max(0.5, Math.min(4, d.zoom || 1));
  const scale = fitScale * zoom;
  const w = iw * scale, h = ih * scale;
  const cx = (width - w) / 2, cy = (height - h) / 2;
  const offX = d.offsetX || 0, offY = d.offsetY || 0;
  // Clip children to zone bounds so pan/zoom cannot overflow the printable area.
  return (
    <Group clipX={0} clipY={0} clipWidth={width} clipHeight={height}>
      <KImage
        image={img}
        width={w} height={h}
        x={cx + offX} y={cy + offY}
        draggable={canMove}
        onDragEnd={(e) => onChange({ offsetX: e.target.x() - cx, offsetY: e.target.y() - cy })}
      />
    </Group>
  );
}

let _measureCtx;
function measureText(text, fontSize, fontFamily) {
  if (!_measureCtx) _measureCtx = document.createElement("canvas").getContext("2d");
  _measureCtx.font = `${fontSize}px "${fontFamily}", system-ui, sans-serif`;
  return _measureCtx.measureText(text).width;
}

// Line-height multiplier applied to every wrapped multi-line text block on the canvas.
// Kept in sync with Konva <Text lineHeight={LINE_HEIGHT}/> so measured height === rendered height.
const LINE_HEIGHT = 1.2;

// Wrap `text` into visual lines that each fit within `maxWidth` at (fontSize, fontFamily).
// Honors hard `\n` newlines from the customer as line breaks, then greedily word-wraps each
// paragraph. If a single word is wider than maxWidth, it is broken at the character level so
// nothing ever overflows horizontally. Always returns at least one line (possibly empty).
function wrapLines(text, fontSize, fontFamily, maxWidth) {
  const limit = Math.max(1, maxWidth * 0.94);
  const paragraphs = String(text || "").split("\n");
  const out = [];
  for (const para of paragraphs) {
    if (!para) { out.push(""); continue; }
    const words = para.split(/(\s+)/); // preserve inter-word whitespace so re-joining is faithful
    let cur = "";
    const pushCur = () => { if (cur.length) { out.push(cur); cur = ""; } };
    for (const w of words) {
      if (!w) continue;
      const trial = cur + w;
      if (measureText(trial, fontSize, fontFamily) <= limit) { cur = trial; continue; }
      // `w` doesn't fit on the current line — flush current, then place `w`.
      pushCur();
      if (measureText(w, fontSize, fontFamily) <= limit) {
        cur = /^\s+$/.test(w) ? "" : w;
      } else {
        // Word alone is too wide — split at character boundaries.
        let chunk = "";
        for (const ch of w) {
          if (measureText(chunk + ch, fontSize, fontFamily) <= limit) { chunk += ch; }
          else { if (chunk) out.push(chunk); chunk = ch; }
        }
        cur = chunk;
      }
    }
    out.push(cur);
  }
  return out.length ? out : [""];
}

// Binary-search the largest font size at which `text` (word-wrapped honoring `\n`) fits inside
// (width × height). Both per-line width AND total block height (lines * fontSize * LINE_HEIGHT)
// must fit. If even at `min` nothing fits, returns `min` (Konva then handles clipping).
function computeFontSize(text, width, height, fontFamily, min = 12, max = 240) {
  if (!text) return min;
  const upper = Math.min(max, Math.floor(height * 0.95));
  let lo = min, hi = upper, best = min;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const lines = wrapLines(text, mid, fontFamily, width);
    const totalH = lines.length * mid * LINE_HEIGHT;
    if (totalH <= height * 0.98) { best = mid; lo = mid + 1; }
    else { hi = mid - 1; }
  }
  return Math.max(min, best);
}

// One-stop layout: returns the chosen font size, the word-wrapped lines at that size, and the
// axis-aligned footprint (boxW × boxH) the rendered text block actually occupies inside the zone.
// The footprint drives drag/rotate clamping so multi-line blocks stay inside the printable area.
function computeTextLayout(text, width, height, fontFamily, min = 12, max = 240) {
  const fontSize = computeFontSize(text, width, height, fontFamily, min, max);
  const lines = wrapLines(text, fontSize, fontFamily, width);
  const widest = lines.reduce((mx, ln) => Math.max(mx, measureText(ln, fontSize, fontFamily)), 0);
  const boxW = Math.min(width, Math.max(1, widest));
  const boxH = Math.min(height, Math.max(fontSize, lines.length * fontSize * LINE_HEIGHT));
  return { fontSize, lines, boxW, boxH };
}

// Clamp a rotatable box's centre (cx, cy — zone-local coords, i.e. relative to the zone's
// own top-left, matching Konva's node.x()/node.y() for a child of the zone Group) so that
// the box (boxW × boxH), rotated by `rot` degrees around that centre, keeps all 4 of its
// corners inside the zone rectangle (0,0)–(zoneW,zoneH) — the printable area. At rotation
// θ, an axis-aligned box of size W×H rotated about its own centre has an axis-aligned
// bounding footprint of (|cos θ|·W + |sin θ|·H) × (|sin θ|·W + |cos θ|·H); clamping the
// centre so that footprint's edges stay within the zone is equivalent to clamping all 4
// rotated corners into the zone. Falls back to dead-centre only when the box is too big to
// ever fully fit at this rotation (minimises unavoidable overflow).
function clampRotatedCenter(cx, cy, rot, boxW, boxH, zoneW, zoneH) {
  const rad = (((rot % 360) + 360) % 360) * Math.PI / 180;
  const ac = Math.abs(Math.cos(rad)), as = Math.abs(Math.sin(rad));
  const hx = (ac * boxW + as * boxH) / 2;
  const hy = (as * boxW + ac * boxH) / 2;
  const cxMin = hx, cxMax = zoneW - hx;
  const cyMin = hy, cyMax = zoneH - hy;
  const validX = cxMin <= cxMax, validY = cyMin <= cyMax;
  return {
    x: validX ? Math.max(cxMin, Math.min(cxMax, cx)) : zoneW / 2,
    y: validY ? Math.max(cyMin, Math.min(cyMax, cy)) : zoneH / 2,
  };
}

function CurvedText({ text, width, height, curve, fontFamily, fontSize, fill }) {
  const cx = width / 2, midY = height / 2;
  const amt = curve / 100;
  const apex = amt * (height * 0.45);
  const startX = 0, endX = width;
  const startY = midY + apex, endY = midY + apex, apexY = midY - apex;
  const samples = 60;
  let arcLen = 0, prevX = startX, prevY = startY;
  for (let i = 1; i <= samples; i++) {
    const t = i / samples, mt = 1 - t;
    const x = mt * mt * startX + 2 * mt * t * cx + t * t * endX;
    const y = mt * mt * startY + 2 * mt * t * apexY + t * t * endY;
    arcLen += Math.hypot(x - prevX, y - prevY); prevX = x; prevY = y;
  }
  const chars = Array.from(text);
  if (chars.length === 0) return null;
  const total = measureText(text, fontSize, fontFamily);
  const usedWidth = Math.min(total, arcLen * 0.98);
  const startOffset = (arcLen - usedWidth) / 2;
  const charWidths = chars.map((ch) => measureText(ch, fontSize, fontFamily));
  const arcPoint = (s) => {
    let acc = 0, pX = startX, pY = startY;
    for (let i = 1; i <= samples; i++) {
      const t = i / samples, mt = 1 - t;
      const x = mt * mt * startX + 2 * mt * t * cx + t * t * endX;
      const y = mt * mt * startY + 2 * mt * t * apexY + t * t * endY;
      const seg = Math.hypot(x - pX, y - pY);
      if (acc + seg >= s) {
        const r = (s - acc) / (seg || 1);
        return { x: pX + (x - pX) * r, y: pY + (y - pY) * r, angle: Math.atan2(y - pY, x - pX) };
      }
      acc += seg; pX = x; pY = y;
    }
    return { x: startX, y: startY, angle: 0 };
  };
  let cursor = startOffset;
  return (
    <>
      {chars.map((ch, i) => {
        const cw = charWidths[i], s = cursor + cw / 2, p = arcPoint(s);
        cursor += cw;
        return (
          <Text key={i} text={ch} x={p.x} y={p.y}
            offsetX={cw / 2} offsetY={fontSize / 2}
            rotation={(p.angle * 180) / Math.PI}
            fontFamily={fontFamily} fontSize={fontSize} fill={fill}/>
        );
      })}
    </>
  );
}

async function loadCustomFonts(customFonts) {
  if (!customFonts || !document.fonts) return;
  for (const cf of customFonts) {
    const family = cf.name;
    if ([...document.fonts].some((f) => f.family === family)) continue;
    try {
      const url = cf.url.startsWith("http") ? cf.url : `${BACKEND_URL}${cf.url}`;
      const face = new FontFace(family, `url(${url})`);
      const loaded = await face.load();
      document.fonts.add(loaded);
    } catch (e) {
      console.warn("font load failed", family, e);
    }
  }
}

export default function Customizer() {
  const { id } = useParams();
  const [tpl, setTpl] = useState(null);
  const [activeView, setActiveView] = useState(0);
  const [design, setDesign] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [googleFonts, setGoogleFonts] = useState([]);
  const [customFontList, setCustomFontList] = useState([]);
  const [scale, setScale] = useState(0.5);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [checkout, setCheckout] = useState(false);
  const [done, setDone] = useState(null);
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", whatsapp: "", notes: "" });
  const [shippingInkColor, setShippingInkColor] = useState("");
  const [previewMode, setPreviewMode] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  const stageRefs = useRef({});
  // Konva refs for buyer rotate handle. Keyed by zone.id → the inner Group (the rotating node).
  const rotateNodeRefs = useRef({});
  const transformerRefs = useRef({});
  // Face Stamp: raw (unprocessed) photo File per zone, kept so the filter dropdown can
  // re-run client-side without asking the customer to re-upload. Keyed by zone.id.
  const rawPhotoFiles = useRef({});
  // Per-zone async "generation" counter — lets a stale background upload (from a filter the
  // customer already switched away from) detect it's outdated and skip clobbering image_url.
  const faceFilterGen = useRef({});

  // Bind the Transformer to its target inner-Group whenever selection / permission changes.
  useEffect(() => {
    if (!selectedId) return;
    // Defer to next tick so refs are populated after the current render commit.
    const raf = requestAnimationFrame(() => {
      const tr = transformerRefs.current[selectedId];
      const node = rotateNodeRefs.current[selectedId];
      if (tr && node) {
        tr.nodes([node]);
        const layer = tr.getLayer && tr.getLayer();
        if (layer) layer.batchDraw();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedId, tpl, activeView, design]);

  useEffect(() => {
    // Support seller "Preview as customer" — pass Authorization header if a preview token was
    // supplied via ?preview_token=... OR the seller is already logged in (cc_token in localStorage).
    const qs = new URLSearchParams(window.location.search);
    const previewToken = qs.get("preview_token") || localStorage.getItem("cc_token") || "";
    const headers = previewToken ? { Authorization: `Bearer ${previewToken}` } : {};
    const isPreview = Boolean(qs.get("preview_token"));
    setPreviewMode(isPreview);

    axios.get(`${API}/public/templates/${id}`, { headers }).then((r) => {
      const t = r.data;
      setTpl(t);
      const init = {};
      (t.views || []).forEach((v) => {
        (v.zones || []).forEach((z) => {
          init[z.id] = z.type === "text"
            ? { type: "text",
                text: z.default_text || "Click to Edit",
                font: z.default_font || "Outfit",
                color: z.default_color || (((t.ink_slots || []).find((s) => s.id === z.ink_slot_id)?.default_color) || (((t.ink_slots || []).find((s) => s.id === z.ink_slot_id)?.colors || [])[0]) || "#1c1917"),
                curve: z.curve || 0, manual_size: z.manual_font_size || 0 }
            : { type: "image", image_url: z.default_image_url || "" };
        });
      });
      setDesign(init);
    }).catch(() => toast.error("Template not found or not published"));

    axios.get(`${API}/fonts`).then(async (r) => {
      setGoogleFonts(r.data.google_fonts || []);
      const cf = r.data.custom_fonts || [];
      setCustomFontList(cf);
      await loadCustomFonts(cf);
      setFontsReady((v) => !v);  // trigger recompute
    });
  }, [id]);

  useEffect(() => {
    if (!document.fonts) { setFontsReady(true); return; }
    document.fonts.ready.then(() => setFontsReady(true));
  }, []);

  useEffect(() => {
    if (!design || !document.fonts) return;
    const fams = new Set();
    Object.values(design).forEach((d) => { if (d?.font) fams.add(d.font); });
    fams.forEach((f) => {
      try { document.fonts.load(`16px "${f}"`).then(() => setFontsReady((v) => v)); }
      catch (_e) { /* ignore */ }
    });
  }, [design]);

  useEffect(() => {
    const fit = () => {
      const cw = window.innerWidth >= 1024 ? Math.min(window.innerWidth * 0.55, 700) : Math.min(window.innerWidth - 40, 800);
      setScale(Math.max(0.3, cw / CANVAS_SIZE));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  const theme = useMemo(() => {
    const t = tpl || {};
    return {
      primary: t.theme_primary_color || "#1c1917",
      accent: t.theme_accent_color || "#fde047",
      borderWidth: t.theme_border_width ?? 1,
      radius: t.theme_border_radius ?? 6,
      uiFont: t.theme_ui_font || "Inter",
    };
  }, [tpl]);

  useEffect(() => {
    if (!theme.uiFont) return;
    const family = theme.uiFont.replace(/ /g, "+");
    const eid = `gf-ui-${family}`;
    if (document.getElementById(eid)) return;
    const link = document.createElement("link");
    link.id = eid; link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${family}:wght@400;600;700&display=swap`;
    document.head.appendChild(link);
  }, [theme.uiFont]);

  if (!tpl) return <div className="min-h-screen flex items-center justify-center bg-stone-50"><div className="brutal-card p-6 font-heading font-bold">Loading design studio…</div></div>;

  const views = tpl.views || [];
  const view = views[activeView] || views[0];
  const setZone = (zid, patch) => setDesign((d) => ({ ...d, [zid]: { ...d[zid], ...patch } }));

  const maxTextLimit = Number(tpl.max_text_limit || 0);
  const isTextTooLong = (val) =>
    maxTextLimit > 0 && (val || "").length > maxTextLimit + 5;
  // Zones (across all views) whose current text overflows the safety cap — hide on canvas & block checkout
  const overflowingZoneIds = new Set();
  views.forEach((v) => (v.zones || []).forEach((z) => {
    if (z.type === "text" && isTextTooLong(design[z.id]?.text)) overflowingZoneIds.add(z.id);
  }));
  const hasOverflow = overflowingZoneIds.size > 0;

  // Union of all ink-slot colors for shipping-only selector
  const shippingInkOptions = (() => {
    const seen = new Map();  // color -> label (first-seen wins)
    (tpl.ink_slots || []).forEach((slot) => {
      (slot.colors || []).forEach((c) => {
        if (!seen.has(c)) seen.set(c, slot.label || "Ink");
      });
    });
    return Array.from(seen.entries()).map(([color, label]) => ({ color, label }));
  })();

  // Selected zone: search all views' zones
  const findZone = (zid) => {
    for (const v of views) {
      const z = (v.zones || []).find((zz) => zz.id === zid);
      if (z) return { view: v, zone: z };
    }
    return { view: null, zone: null };
  };
  const selected = selectedId ? findZone(selectedId).zone : null;
  const selectedDesign = selectedId ? design[selectedId] : null;

  const allFonts = [...googleFonts, ...customFontList.map((c) => c.name)];

  // Face Stamp: the seller's allow-listed photo filters (falls back to every filter for older
  // templates saved before this setting existed), in the shared FACE_FILTERS display order.
  const allowedFaceFilterKeys = (tpl.face_stamp_filters && tpl.face_stamp_filters.length)
    ? tpl.face_stamp_filters
    : FACE_FILTERS.map((f) => f.key);
  const allowedFaceFilters = FACE_FILTERS.filter((f) => allowedFaceFilterKeys.includes(f.key));

  // Runs a filter (client-side canvas only, never an AI service) against the zone's stored raw
  // photo. Shows the result INSTANTLY via a local object URL (true live preview, no network
  // wait), then silently swaps to the permanently-hosted URL once the background upload
  // finishes — so order submission still works exactly like before. A per-zone generation
  // counter discards a stale upload if the customer switches filters again before it lands.
  const applyFaceFilter = async (zid, filterKey, file) => {
    const gen = (faceFilterGen.current[zid] || 0) + 1;
    faceFilterGen.current[zid] = gen;
    try {
      const blob = await sketchify(file, { filter: filterKey });
      if (faceFilterGen.current[zid] !== gen) return;
      const localUrl = URL.createObjectURL(blob);
      setZone(zid, { face_filter: filterKey, image_url: localUrl });
      const fd = new FormData();
      fd.append("file", blob, `photo-${filterKey}.png`);
      const { data } = await axios.post(`${API}/public-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (faceFilterGen.current[zid] !== gen) return;
      setZone(zid, { image_url: fileUrl(data.url) });
    } catch (err) {
      console.warn("face filter apply failed", err);
      toast.error("Filter failed — try another one");
    }
  };

  const onUpload = async (zid, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error("Max 50MB"); return; }
    const zone = findZone(zid).zone;
    const minDim = zone?.min_image_dimension ?? 300;
    const dim = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.width, h: img.height });
      img.onerror = () => resolve({ w: 0, h: 0 });
      img.src = URL.createObjectURL(file);
    });
    if (dim.w < minDim || dim.h < minDim) { toast.error(`Image must be at least ${minDim}×${minDim} px`); return; }
    setUploading(true);
    try {
      if (tpl.category === "face_stamp") {
        rawPhotoFiles.current[zid] = file;
        // Default to the first non-"no effect" filter the seller enabled, so the photo→sketch
        // wow-factor still shows by default; falls back to "no effect" if that's all there is.
        const defaultFilter = (allowedFaceFilters.find((f) => f.key !== "no_effect") || allowedFaceFilters[0] || { key: "no_effect" }).key;
        await applyFaceFilter(zid, defaultFilter, file);
        toast.success("Photo uploaded — pick a filter below");
      } else {
        const fd = new FormData();
        fd.append("file", file, file.name);
        const { data } = await axios.post(`${API}/public-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setZone(zid, { image_url: fileUrl(data.url) });
        toast.success("Image uploaded");
      }
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const submit = async () => {
    if (previewMode) {
      toast.info("Preview mode — orders are disabled");
      return;
    }
    if (!customer.name || !customer.email) { toast.error("Name and email required"); return; }
    if (tpl.whatsapp_required !== false && !customer.whatsapp.trim()) {
      toast.error("WhatsApp number required for final design confirmation");
      return;
    }
    if (hasOverflow) {
      toast.error("Some text layers exceed the allowed length. Shorten them before adding to cart.");
      return;
    }
    if (shippingInkOptions.length > 0 && !shippingInkColor) {
      toast.error("Please select the ink color for shipping.");
      return;
    }
    setSubmitting(true);
    try {
      const previews = [];
      // 2000×2000 high-res output: canvas native = 1000, stage is scaled to `scale`, so
      // pixelRatio = 2 / scale ⇒ (1000 * scale) * (2 / scale) = 2000 px per side.
      const hiResPixelRatio = 2 / (scale || 0.5);
      for (const v of views) {
        const s = stageRefs.current[v.id];
        if (s && s.toDataURL) {
          previews.push(s.toDataURL({ pixelRatio: hiResPixelRatio, mimeType: "image/png" }));
        }
      }
      if (previews.length === 0 || previews.length < views.length) {
        toast.error("Couldn't capture your design preview — please wait a second and try again.");
        setSubmitting(false);
        return;
      }
      const design_data = {};
      views.forEach((v) => (v.zones || []).forEach((z) => {
        design_data[z.id] = { ...design[z.id], label: z.label, type: z.type, view_id: v.id, view_name: v.name };
      }));
      const { data } = await axios.post(`${API}/orders`, {
        template_id: tpl.id,
        customer_name: customer.name, customer_email: customer.email,
        customer_phone: customer.phone, customer_whatsapp: customer.whatsapp,
        shipping_ink_color: shippingInkColor || "",
        notes: customer.notes,
        design_data, preview_data_urls: previews,
        origin_url: window.location.origin,
      });
      setDone(data);
      if (data.shopify_error) {
        // SKU didn't resolve on Shopify (or Shopify rejected the request). Order is saved,
        // but we intentionally do NOT redirect — the buyer sees the exact error.
        toast.error(data.shopify_error.message || "Checkout could not be created on Shopify. Order saved.", { duration: 10000 });
      } else if (data.shopify_checkout_url) {
        toast.success("Order saved — redirecting to checkout…");
        if (!previewMode) {
          setTimeout(() => {
            try { window.top.location.href = data.shopify_checkout_url; }
            catch (_) { window.location.href = data.shopify_checkout_url; }
          }, 600);
        }
      } else {
        toast.success("Order saved.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    }
    setSubmitting(false);
  };

  const cardStyle = { background: "#fff", border: `${theme.borderWidth}px solid ${theme.primary}`, borderRadius: theme.radius, fontFamily: `"${theme.uiFont}", system-ui, sans-serif` };
  const btnPrimary = { background: theme.accent, color: theme.primary, border: `${theme.borderWidth}px solid ${theme.primary}`, borderRadius: theme.radius, fontWeight: 700, padding: "10px 18px", cursor: "pointer", fontFamily: `"${theme.uiFont}", system-ui, sans-serif` };
  const btnDark = { ...btnPrimary, background: theme.primary, color: "#fff" };
  const btnGhost = { ...btnPrimary, background: "#fff" };
  const inputStyle = { background: "#fff", border: `${theme.borderWidth}px solid ${theme.primary}`, borderRadius: theme.radius, padding: "10px 12px", outline: "none", width: "100%", fontFamily: `"${theme.uiFont}", system-ui, sans-serif`, fontSize: 14 };

  if (done) {
    const priceStr = "";
    const hasShopifyError = !!done.shopify_error;
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: hasShopifyError ? "#fee2e2" : theme.accent, fontFamily: `"${theme.uiFont}", system-ui, sans-serif` }}>
        <div style={{ ...cardStyle, padding: 32, maxWidth: 520, textAlign: "center" }} data-testid={hasShopifyError ? "order-done-error" : "order-done-success"}>
          <div style={{ width: 64, height: 64, background: hasShopifyError ? "#fecaca" : theme.accent, border: `${theme.borderWidth}px solid ${theme.primary}`, borderRadius: theme.radius, margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {hasShopifyError ? <span style={{ fontSize: 32, fontWeight: 900, color: "#991b1b" }}>!</span> : <Check className="w-8 h-8" style={{ color: theme.primary }}/>}
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: 0, color: hasShopifyError ? "#991b1b" : theme.primary }}>
            {hasShopifyError ? "Checkout couldn't be created" : (done.shopify_checkout_url ? "Redirecting to checkout…" : "Added to cart!")}
          </h1>
          <p style={{ color: theme.primary, opacity: 0.85, marginTop: 8, fontSize: 14 }}>
            {hasShopifyError ? (
              <>Your design was saved <code style={{ background: "#f5f5f4", padding: "2px 6px", borderRadius: 4 }}>{done.id.slice(0,8)}</code>, but Shopify rejected the checkout. Please share this with the seller so they can fix it:</>
            ) : (
              <>Order <code style={{ background: "#f5f5f4", padding: "2px 6px", borderRadius: 4 }}>{done.id.slice(0,8)}</code>{priceStr && ` · ${priceStr}`}. The seller has your design.</>
            )}
          </p>
          {hasShopifyError && (
            <div style={{ marginTop: 12, padding: 12, background: "#fff1f2", border: "1px solid #fca5a5", borderRadius: theme.radius, textAlign: "left", fontSize: 12, color: "#7f1d1d" }} data-testid="shopify-error-message">
              <b>{done.shopify_error.code}</b>: {done.shopify_error.message}
            </div>
          )}
          {done.preview_url && !hasShopifyError && <img src={fileUrl(done.preview_url)} alt="preview" style={{ width: "100%", aspectRatio: "1/1", objectFit: "contain", border: `${theme.borderWidth}px solid ${theme.primary}`, borderRadius: theme.radius, margin: "16px 0", background: "#fff" }}/>}
          <div style={{ marginTop: 16 }}>
            <button onClick={() => (window.location.href = `/c/${tpl.id}`)} style={btnPrimary} data-testid="design-another">
              {hasShopifyError ? "Try again" : "Design another"}
            </button>
            {tpl.product_url && (
              <a href={tpl.product_url} target="_blank" rel="noreferrer" style={{ ...btnGhost, display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 8, textDecoration: "none" }}>
                <ExternalLink className="w-4 h-4"/> Product page
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#fafaf9", fontFamily: `"${theme.uiFont}", system-ui, sans-serif`, color: theme.primary }} data-fonts-ready={fontsReady ? "1" : "0"}>
      <header style={{ background: "#fff", borderBottom: `${theme.borderWidth}px solid ${theme.primary}` }}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div style={{ width: 36, height: 36, background: theme.accent, border: `${theme.borderWidth}px solid ${theme.primary}`, borderRadius: theme.radius, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Palette className="w-5 h-5" style={{ color: theme.primary }}/>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 18, lineHeight: 1 }}>{tpl.name}</div>
              <div style={{ fontSize: 11, opacity: 0.65, marginTop: 2 }}>Customize · Preview · Buy</div>
            </div>
          </div>
          <button onClick={() => setCheckout(true)}
            disabled={hasOverflow}
            style={{ ...btnDark, opacity: hasOverflow ? 0.5 : 1, cursor: hasOverflow ? "not-allowed" : "pointer" }}
            className="inline-flex items-center gap-2 text-sm" data-testid="open-checkout">
            <ShoppingCart className="w-4 h-4"/> Add to cart
          </button>
        </div>
      </header>

      {previewMode && (
        <div className="max-w-6xl mx-auto px-4 pt-4">
          <div style={{ ...cardStyle, padding: 10, background: "#fef3c7", borderColor: "#a16207" }} data-testid="preview-mode-banner">
            <div style={{ fontSize: 12, color: "#854d0e", fontWeight: 700 }}>
              Preview mode — you&apos;re viewing this template exactly as a customer will. Ordering is disabled.
            </div>
          </div>
        </div>
      )}

      {hasOverflow && (
        <div className="max-w-6xl mx-auto px-4 pt-4">
          <div style={{ ...cardStyle, padding: 12, background: "#fef2f2", borderColor: "#dc2626" }} data-testid="global-overflow-warn">
            <div style={{ fontSize: 13, color: "#991b1b", fontWeight: 700 }}>
              One or more text layers exceed the safe length. Those layers are hidden from the preview and checkout is disabled until you shorten them.
            </div>
          </div>
        </div>
      )}

      {tpl.notice && (
        <div className="max-w-6xl mx-auto px-4 pt-4">
          <div style={{ ...cardStyle, padding: 12, background: "#eef2ff" }} data-testid="tpl-notice">
            <div style={{ fontSize: 13 }}>ℹ️ {tpl.notice}</div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-6 grid lg:grid-cols-[1fr_360px] gap-6">
        {/* CANVAS + VIEW TABS */}
        <div style={{ ...cardStyle, padding: 16 }}>
          {views.length > 1 && (
            <div className="flex gap-2 mb-3 flex-wrap">
              {views.map((v, i) => (
                <button key={v.id} onClick={() => setActiveView(i)}
                  style={{ ...btnGhost, background: i === activeView ? theme.accent : "#fff", padding: "6px 12px", fontSize: 13 }}
                  data-testid={`view-tab-${i}`}>{v.name}</button>
              ))}
            </div>
          )}
          {/* Render each view but only show the active one — this lets us capture stage refs for all */}
          {views.map((v, i) => (
            <div key={v.id} style={{ display: i === activeView ? "block" : "none" }}>
              <div className="flex items-center justify-center rounded-md p-2" style={{ minHeight: 400, background: "#f5f5f4", border: `${theme.borderWidth}px dashed ${theme.primary}`, borderRadius: theme.radius }}>
                <div style={{ width: CANVAS_SIZE * scale, height: CANVAS_SIZE * scale }}>
                  <Stage ref={(el) => (stageRefs.current[v.id] = el)}
                    width={CANVAS_SIZE * scale} height={CANVAS_SIZE * scale}
                    scaleX={scale} scaleY={scale}>
                    <Layer>
                      <Rect width={CANVAS_SIZE} height={CANVAS_SIZE} fill={v.bg_color || "#ffffff"} />
                      {v.base_image_url && <BaseImage url={v.base_image_url} />}
                      {(v.zones || []).map((z) => {
                        const d = design[z.id] || {};
                        const isBackend = z.customer_editable === false;
                        const isText = z.type === "text";
                        // Buyer-controlled interactivity (only for text, only if seller opted in per-permission).
                        const canMoveText = isText && !isBackend && !!z.buyer_can_move;
                        const canRotateFree = isText && !isBackend && !!z.buyer_can_rotate;
                        const canResize = isText && !isBackend && !!z.buyer_can_resize;
                        const isSelected = !isBackend && selectedId === z.id;
                        // Effective rotation: buyer's slider takes over if allowed, else seller's base rotation.
                        const rot = isText ? (canRotateFree ? (d.buyer_rotation ?? z.rotation ?? 0) : (z.rotation || 0)) : 0;
                        // Buyer position offset (only if allowed) — clamped to keep the text centre inside the zone.
                        const bOffX = canMoveText ? (d.buyer_offsetX || 0) : 0;
                        const bOffY = canMoveText ? (d.buyer_offsetY || 0) : 0;
                        return (
                          <Group key={z.id} x={z.x} y={z.y}
                            onClick={isBackend ? undefined : () => setSelectedId(z.id)}
                            onTap={isBackend ? undefined : () => setSelectedId(z.id)}
                            listening={!isBackend}>
                            {!isBackend && selectedId === z.id && (
                              <Rect width={z.width} height={z.height} stroke={theme.primary} strokeWidth={2} dash={[8, 6]} fill="rgba(253,224,71,0.12)"/>
                            )}
                            {isText && d.text && !overflowingZoneIds.has(z.id) && (() => {
                              const _ = fontsReady;
                              const buyerSize = d.buyer_font_size || 0;
                              const manual = buyerSize > 0 ? buyerSize : (d.manual_size || z.manual_font_size || 0);
                              const curve = d.curve || 0;
                              const isCurved = Math.abs(curve) > 5;
                              // Auto-fit produces (fontSize, lines, boxW, boxH) in one pass; manual
                              // sizing skips the search but still measures the real multi-line block.
                              let fs, lines, boxW, boxH;
                              if (manual > 0) {
                                fs = manual;
                                lines = wrapLines(d.text, fs, d.font, z.width);
                                const widest = lines.reduce((mx, ln) => Math.max(mx, measureText(ln, fs, d.font)), 0);
                                boxW = isCurved ? z.width : Math.min(z.width, Math.max(1, widest));
                                boxH = isCurved ? z.height : Math.min(z.height, Math.max(fs, lines.length * fs * LINE_HEIGHT));
                              } else {
                                const layout = computeTextLayout(d.text, z.width, z.height, d.font, z.min_font_size || 12, z.max_font_size || 240);
                                fs = layout.fontSize;
                                lines = layout.lines;
                                boxW = isCurved ? z.width : layout.boxW;
                                boxH = isCurved ? z.height : layout.boxH;
                              }
                              const inner = isCurved
                                ? <CurvedText key={`${z.id}-${_}`} text={d.text} width={z.width} height={z.height} curve={curve} fontFamily={d.font} fontSize={fs} fill={d.color}/>
                                : <Text key={`${z.id}-${_}-${fs}-${lines.length}`} width={z.width} height={z.height} align="center" verticalAlign="middle"
                                    text={d.text} fontFamily={d.font} fontSize={fs} fill={d.color}
                                    wrap="word" lineHeight={LINE_HEIGHT} ellipsis={true}/>;
                              // The multi-line block's own rendered footprint is boxW × boxH (computed
                              // above). Curved text's true footprint is hard to measure exactly, so it
                              // conservatively falls back to the zone's own size.
                              // Wrap in a rotating/movable inner group so drag origin is the zone-space (0,0),
                              // and rotation pivots around the zone centre.
                              const drag = canMoveText ? {
                                draggable: true,
                                onDragMove: (e) => {
                                  // Konva's dragBoundFunc works in absolute STAGE coordinates (which include the
                                  // canvas's display scale), a different frame than z.x/z.width. Using the
                                  // 'dragmove' event instead keeps everything in the same zone-local frame as
                                  // e.target.x()/y() (0,0 = zone top-left) — the same frame clampRotatedCenter
                                  // and the rotation handlers already use, so drag + rotate share one source of truth.
                                  const c = clampRotatedCenter(e.target.x(), e.target.y(), rot, boxW, boxH, z.width, z.height);
                                  e.target.x(c.x);
                                  e.target.y(c.y);
                                },
                                onDragEnd: (e) => {
                                  const cx = z.width / 2, cy = z.height / 2;
                                  setZone(z.id, { buyer_offsetX: e.target.x() - cx, buyer_offsetY: e.target.y() - cy });
                                },
                              } : {};
                              // Inner group anchors rotation around zone centre AND is the draggable/rotatable/resizable node.
                              const minS = z.min_font_size || 12, maxS = z.max_font_size || 240;
                              // Unified handler for both the rotate anchor AND the 4 corner resize anchors on the
                              // same Transformer. Konva only moves ONE anchor per gesture, so scale changes and
                              // rotation changes never both happen in a single call — but we recompute whichever
                              // changed and re-derive the OTHER dimension's footprint fresh so the two stay in sync.
                              // Resize: a live scale factor is translated into a real font size (re-measuring the
                              // word-wrapped block at that size, honoring min/max font size), then the Konva scale
                              // is reset to 1 so the size lives only in `buyer_font_size`, never as a lingering
                              // node transform. Both operations funnel through the SAME clampRotatedCenter used by
                              // drag, so the block can never leave the printable zone — rotated or not.
                              const handleTextTransform = (e) => {
                                const angle = ((e.target.rotation() % 360) + 360) % 360;
                                const rounded = Math.round(angle);
                                const scale = e.target.scaleX() || 1;
                                const resized = canResize && Math.abs(scale - 1) > 0.001;
                                let newFs = fs, newBoxW = boxW, newBoxH = boxH;
                                if (resized) {
                                  const tentativeFs = fs * scale;
                                  if (isCurved) {
                                    newFs = Math.max(minS, Math.min(maxS, tentativeFs));
                                    newBoxW = z.width; newBoxH = z.height;
                                  } else {
                                    const desiredMax = Math.max(minS, Math.min(maxS, tentativeFs));
                                    newFs = computeFontSize(d.text, z.width, z.height, d.font, minS, desiredMax);
                                    const newLines = wrapLines(d.text, newFs, d.font, z.width);
                                    const widest = newLines.reduce((mx, ln) => Math.max(mx, measureText(ln, newFs, d.font)), 0);
                                    newBoxW = Math.min(z.width, Math.max(1, widest));
                                    newBoxH = Math.min(z.height, Math.max(newFs, newLines.length * newFs * LINE_HEIGHT));
                                  }
                                }
                                const c = clampRotatedCenter(e.target.x(), e.target.y(), rounded, newBoxW, newBoxH, z.width, z.height);
                                e.target.x(c.x);
                                e.target.y(c.y);
                                e.target.scaleX(1);
                                e.target.scaleY(1);
                                const patch = {};
                                if (rounded !== (design[z.id]?.buyer_rotation ?? -1)) patch.buyer_rotation = rounded;
                                if (resized) patch.buyer_font_size = newFs;
                                if (canMoveText) { patch.buyer_offsetX = c.x - z.width / 2; patch.buyer_offsetY = c.y - z.height / 2; }
                                setZone(z.id, patch);
                              };
                              return (
                                <>
                                  <Group
                                    ref={(node) => { rotateNodeRefs.current[z.id] = node; }}
                                    x={z.width / 2 + bOffX} y={z.height / 2 + bOffY}
                                    rotation={rot}
                                    offsetX={z.width / 2} offsetY={z.height / 2}
                                    {...drag}
                                  >
                                    {inner}
                                  </Group>
                                  {isSelected && (canRotateFree || canResize) && (
                                    <Transformer
                                      ref={(node) => { transformerRefs.current[z.id] = node; }}
                                      resizeEnabled={canResize}
                                      rotateEnabled={canRotateFree}
                                      enabledAnchors={canResize ? ["top-left", "top-right", "bottom-left", "bottom-right"] : []}
                                      keepRatio={true}
                                      centeredScaling={true}
                                      rotateAnchorOffset={28}
                                      borderStroke={theme.primary}
                                      borderStrokeWidth={2}
                                      borderDash={[6, 4]}
                                      anchorStroke={theme.primary}
                                      anchorFill="#fde047"
                                      anchorSize={12}
                                      anchorCornerRadius={999}
                                      rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
                                      rotationSnapTolerance={4}
                                      onTransform={handleTextTransform}
                                      onTransformEnd={handleTextTransform}
                                    />
                                  )}
                                </>
                              );
                            })()}
                            {z.type === "image" && d.image_url && (
                              <EditableZoneImage
                                url={d.image_url}
                                width={z.width}
                                height={z.height}
                                d={d}
                                canMove={!isBackend && z.allow_move !== false}
                                onChange={(patch) => setZone(z.id, patch)}
                              />
                            )}
                            {z.type === "image" && !d.image_url && (
                              <>
                                <Rect width={z.width} height={z.height} fill="rgba(251,207,232,0.35)" stroke={theme.primary} strokeWidth={2} dash={[8,6]}/>
                                <Text width={z.width} height={z.height} align="center" verticalAlign="middle"
                                  text={z.allow_upload === false ? "🔒 Image set by seller" : "Tap to upload image"}
                                  fontSize={Math.min(36, z.height/6)} fontFamily={theme.uiFont} fill={theme.primary}/>
                              </>
                            )}
                          </Group>
                        );
                      })}
                    </Layer>
                  </Stage>
                </div>
              </div>
            </div>
          ))}
          <p style={{ fontSize: 12, textAlign: "center", opacity: 0.7, marginTop: 8 }}>Tap a layer below to edit it</p>
        </div>

        {/* TOOLKIT */}
        <div className="space-y-4">
          <div style={{ ...cardStyle, padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 2, marginBottom: 10 }}>Layers in &ldquo;{view?.name || "View"}&rdquo;</div>
            <div className="grid grid-cols-1 gap-2">
              {(view?.zones || []).filter((z) => z.customer_editable !== false).map((z) => (
                <button key={z.id} onClick={() => setSelectedId(z.id)}
                  style={{ ...btnGhost, background: selectedId === z.id ? theme.accent : "#fff", fontSize: 14, padding: "10px 12px", textAlign: "left" }}
                  className="flex items-center gap-2" data-testid={`pick-zone-${z.id}`}>
                  {z.type === "text" ? <Type className="w-4 h-4"/> : <ImageIcon className="w-4 h-4"/>}
                  <span className="truncate">{z.label || (z.type === "text" ? "Text" : "Image")}</span>
                  {z.type === "image" && z.allow_upload === false && <Lock className="w-3 h-3 ml-auto opacity-60"/>}
                </button>
              ))}
              {(view?.zones || []).filter((z) => z.customer_editable !== false).length === 0 && (
                <div style={{ opacity: 0.65, fontSize: 13 }}>No editable layers on this view.</div>
              )}
            </div>
          </div>

          {shippingInkOptions.length > 0 && (
            <div style={{ ...cardStyle, padding: 16 }} data-testid="shipping-ink-card">
              <label style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 2, display: "block", marginBottom: 6 }}>
                Select Ink Color for Shipping <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <select
                required
                value={shippingInkColor}
                onChange={(e) => setShippingInkColor(e.target.value)}
                style={{ ...inputStyle, borderColor: shippingInkColor ? theme.primary : "#dc2626" }}
                data-testid="shipping-ink-select"
              >
                <option value="">— Choose an ink color —</option>
                {shippingInkOptions.map((opt, i) => (
                  <option key={`${opt.color}-${i}`} value={opt.color}>
                    {opt.label} · {opt.color}
                  </option>
                ))}
              </select>
              {shippingInkColor && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12 }} data-testid="shipping-ink-preview">
                  <span style={{ width: 22, height: 22, background: shippingInkColor, border: `${theme.borderWidth}px solid ${theme.primary}`, borderRadius: theme.radius, display: "inline-block" }}/>
                  <span>Selected: <b>{shippingInkColor}</b></span>
                </div>
              )}
              <p style={{ fontSize: 11, opacity: 0.7, marginTop: 6 }}>
                For shipping/production reference only — this choice does not change the preview above.
              </p>
            </div>
          )}

          {selected && selectedDesign?.type === "text" && (() => {
            const val = selectedDesign.text || "";
            const len = val.length;
            const overLimit = maxTextLimit > 0 && len >= maxTextLimit;
            const nearLimit = maxTextLimit > 0 && !overLimit && len >= maxTextLimit - 2;
            const overflowSafety = isTextTooLong(val);
            const counterColor = overLimit || overflowSafety ? "#dc2626" : nearLimit ? "#ca8a04" : "#57534e";
            // Same footprint math as the on-canvas Konva handlers (measured text box, not the
            // full zone) — used so the rotation SLIDER/PRESET BUTTONS clamp exactly like
            // dragging or using the on-canvas rotate handle does, instead of bypassing it.
            const applyRotation = (deg) => {
              const sBuyerSize = selectedDesign.buyer_font_size || 0;
              const sManual = sBuyerSize > 0 ? sBuyerSize : (selectedDesign.manual_size || selected.manual_font_size || 0);
              const sCurved = Math.abs(selectedDesign.curve || 0) > 5;
              let sBoxW, sBoxH;
              if (sCurved) { sBoxW = selected.width; sBoxH = selected.height; }
              else if (sManual > 0) {
                const sLines = wrapLines(selectedDesign.text, sManual, selectedDesign.font, selected.width);
                const widest = sLines.reduce((mx, ln) => Math.max(mx, measureText(ln, sManual, selectedDesign.font)), 0);
                sBoxW = Math.min(selected.width, Math.max(1, widest));
                sBoxH = Math.min(selected.height, Math.max(sManual, sLines.length * sManual * LINE_HEIGHT));
              } else {
                const layout = computeTextLayout(selectedDesign.text, selected.width, selected.height, selectedDesign.font, selected.min_font_size || 12, selected.max_font_size || 240);
                sBoxW = layout.boxW; sBoxH = layout.boxH;
              }
              const curCx = selected.width / 2 + (selected.buyer_can_move ? (selectedDesign.buyer_offsetX || 0) : 0);
              const curCy = selected.height / 2 + (selected.buyer_can_move ? (selectedDesign.buyer_offsetY || 0) : 0);
              const c = clampRotatedCenter(curCx, curCy, deg, sBoxW, sBoxH, selected.width, selected.height);
              setZone(selected.id, {
                buyer_rotation: deg,
                buyer_offsetX: c.x - selected.width / 2,
                buyer_offsetY: c.y - selected.height / 2,
              });
            };
            return (
            <div style={{ ...cardStyle, padding: 16 }} className="space-y-3" data-testid="text-tools">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 2 }}>{selected.label || "Text"}</div>
                {maxTextLimit > 0 && (
                  <div style={{ fontSize: 12, fontWeight: 700, color: counterColor }} data-testid="text-counter">
                    {len} / {maxTextLimit}
                  </div>
                )}
              </div>
              <textarea rows={Math.min(4, Math.max(1, selected.max_lines || 1))} value={val}
                maxLength={maxTextLimit > 0 ? maxTextLimit : undefined}
                onChange={(e) => {
                  let next = e.target.value;
                  const maxLines = Math.max(1, selected.max_lines || 1);
                  const maxCharsPerLine = selected.max_chars_per_line || 0;
                  // Enforce max lines
                  const parts = next.split("\n");
                  if (parts.length > maxLines) parts.length = maxLines;
                  // Enforce max chars per line
                  const clipped = parts.map((line) => (maxCharsPerLine > 0 ? line.slice(0, maxCharsPerLine) : line));
                  next = clipped.join("\n");
                  setZone(selected.id, { text: next });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (selected.max_lines || 1) <= 1) e.preventDefault();
                }}
                style={{ ...inputStyle, resize: "none", borderColor: overflowSafety ? "#dc2626" : inputStyle.border }}
                placeholder="Your text…" data-testid="text-input"/>
              <div style={{ fontSize: 11, opacity: 0.7, display: "flex", justifyContent: "space-between" }}>
                <span>
                  {(selected.max_lines || 1) > 1 ? `Up to ${selected.max_lines} lines` : "Single line"}
                  {selected.max_chars_per_line ? ` · ${selected.max_chars_per_line} chars/line max` : ""}
                </span>
                {selected.rotation ? <span data-testid="text-rotation-badge">Rotated {selected.rotation}°</span> : null}
              </div>
              {overflowSafety && (
                <div style={{ padding: 10, background: "#fef2f2", border: "1px solid #dc2626", borderRadius: theme.radius, fontSize: 12, color: "#991b1b" }} data-testid="text-overflow-warn">
                  Text exceeds the safe limit — this layer is hidden from the preview and checkout is disabled until you shorten it.
                </div>
              )}
              {selected.allow_font_change && (() => {
                const allowed = (selected.allowed_fonts || []).filter(Boolean);
                const useFilter = allowed.length > 0;
                const gFonts = useFilter ? googleFonts.filter((f) => allowed.includes(f)) : googleFonts;
                const cFonts = useFilter ? customFontList.filter((f) => allowed.includes(f.name)) : customFontList;
                return (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", display: "block", marginBottom: 4 }}>
                    Font{useFilter ? ` · ${gFonts.length + cFonts.length} allowed` : ` · Google Fonts${customFontList.length ? " + custom" : ""}`}
                  </label>
                  <select style={inputStyle} value={selectedDesign.font} onChange={(e) => setZone(selected.id, { font: e.target.value })} data-testid="font-select">
                    {gFonts.length > 0 && (
                      <optgroup label={useFilter ? "Allowed fonts" : "Google Fonts"}>
                        {gFonts.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
                      </optgroup>
                    )}
                    {cFonts.length > 0 && (
                      <optgroup label="Custom fonts">
                        {cFonts.map((f) => <option key={f.id} value={f.name} style={{ fontFamily: f.name }}>{f.name}</option>)}
                      </optgroup>
                    )}
                  </select>
                </div>
                );
              })()}
              {selected.allow_color_change && (() => {
                const allowed = (selected.allowed_colors || []).filter(Boolean);
                const inkSlot = (tpl.ink_slots || []).find((s) => s.id === selected.ink_slot_id);
                // Precedence: per-zone allowed_colors ▸ ink slot ▸ template palette
                const colors = allowed.length > 0 ? allowed : (inkSlot ? (inkSlot.colors || []) : (tpl.palette || []));
                const label = allowed.length > 0 ? "Allowed colors" : (inkSlot ? inkSlot.label : "Color");
                return (
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", display: "block", marginBottom: 6 }}>
                      {label} · {colors.length} option{colors.length !== 1 ? "s" : ""}
                    </label>
                    <div className="grid grid-cols-8 gap-1">
                      {colors.map((c) => (
                        <button key={c} onClick={() => setZone(selected.id, { color: c })} title={c}
                          style={{ width: 28, height: 28, border: `${theme.borderWidth}px solid ${theme.primary}`, borderRadius: theme.radius, background: c, outline: selectedDesign.color === c ? `2px solid ${theme.accent}` : "none", cursor: "pointer" }}
                          data-testid={`color-swatch-${c}`}/>
                      ))}
                      {colors.length === 0 && (
                        <div style={{ gridColumn: "span 8", fontSize: 12, opacity: 0.7 }}>No colors configured.</div>
                      )}
                    </div>
                    {allowed.length === 0 && !inkSlot && (
                      <input type="color" value={selectedDesign.color} onChange={(e) => setZone(selected.id, { color: e.target.value })} style={{ marginTop: 8, width: "100%", height: 40, border: `${theme.borderWidth}px solid ${theme.primary}`, borderRadius: theme.radius }}/>
                    )}
                  </div>
                );
              })()}
              {selected.allow_curve && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", display: "block", marginBottom: 4 }}>Curve: {selectedDesign.curve || 0}</label>
                  <input type="range" min={-100} max={100} value={selectedDesign.curve || 0} onChange={(e) => setZone(selected.id, { curve: +e.target.value })} className="w-full" data-testid="curve-slider"/>
                  <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                    <button style={{ ...btnGhost, padding: "4px 8px", fontSize: 12 }} onClick={() => setZone(selected.id, { curve: -60 })}>Frown</button>
                    <button style={{ ...btnGhost, padding: "4px 8px", fontSize: 12 }} onClick={() => setZone(selected.id, { curve: 0 })}>Flat</button>
                    <button style={{ ...btnGhost, padding: "4px 8px", fontSize: 12 }} onClick={() => setZone(selected.id, { curve: 60 })}>Smile</button>
                  </div>
                </div>
              )}
              {selected.buyer_can_resize && (() => {
                const minS = selected.min_font_size || 12;
                const maxS = selected.max_font_size || 240;
                const current = selectedDesign.buyer_font_size || 0;
                return (
                  <div data-testid="buyer-size-block">
                    <label style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", display: "block", marginBottom: 4 }}>
                      Size · {current > 0 ? `${current} px` : "auto-fit"} <span style={{ fontWeight: 400, opacity: 0.6 }}>({minS}-{maxS} px)</span>
                    </label>
                    <input type="range" min={minS} max={maxS} step={1}
                      value={current > 0 ? current : Math.round((minS + maxS) / 2)}
                      onChange={(e) => setZone(selected.id, { buyer_font_size: +e.target.value })}
                      className="w-full" data-testid="buyer-size-slider"/>
                    {current > 0 && (
                      <button onClick={() => setZone(selected.id, { buyer_font_size: 0 })} style={{ ...btnGhost, padding: "4px 8px", fontSize: 11, marginTop: 4 }} data-testid="buyer-size-auto-btn">
                        Back to auto-fit
                      </button>
                    )}
                  </div>
                );
              })()}
              {selected.buyer_can_rotate && (
                <div data-testid="buyer-rotation-block">
                  <label style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", display: "block", marginBottom: 4 }}>
                    Rotation · <span data-testid="buyer-rotation-degrees">{selectedDesign.buyer_rotation ?? selected.rotation ?? 0}°</span>
                  </label>
                  <input type="range" min={0} max={360} step={1}
                    value={selectedDesign.buyer_rotation ?? selected.rotation ?? 0}
                    onChange={(e) => applyRotation(+e.target.value)}
                    className="w-full" data-testid="buyer-rotation-slider"/>
                  <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                    {[0, 90, 180, 270].map((deg) => (
                      <button key={deg} onClick={() => applyRotation(deg)}
                        style={{ ...btnGhost, padding: "4px 8px", fontSize: 11 }}
                        data-testid={`buyer-rotation-preset-${deg}`}>
                        {deg}°
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {selected.buyer_can_move && (
                <div style={{ fontSize: 11, opacity: 0.75 }} data-testid="buyer-move-hint">
                  Tip: drag the text directly on the canvas to reposition — it stays inside the printable area.
                </div>
              )}
              {selected.allow_reset !== false && (
                <button
                  onClick={() => setZone(selected.id, {
                    text: selected.default_text || "",
                    font: selected.default_font || "Outfit",
                    color: selected.default_color || "#111111",
                    curve: 0,
                    buyer_offsetX: 0,
                    buyer_offsetY: 0,
                    buyer_font_size: 0,
                    buyer_rotation: selected.rotation || 0,
                  })}
                  style={{ ...btnGhost, fontSize: 12, padding: "6px 10px" }}
                  data-testid="reset-text-btn"
                >Reset to default</button>
              )}
            </div>
            );
          })()}

          {selected && selectedDesign?.type === "image" && (
            <div style={{ ...cardStyle, padding: 16 }} className="space-y-3" data-testid="image-tools">
              <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 2 }}>{selected.label || "Image"}</div>
              {selectedDesign.image_url && <img src={selectedDesign.image_url} alt="preview" style={{ width: "100%", aspectRatio: "1/1", objectFit: "contain", border: `${theme.borderWidth}px solid ${theme.primary}`, borderRadius: theme.radius, background: "#fff" }}/>}
              {selected.allow_upload === false ? (
                <div style={{ padding: 12, background: "#f5f5f4", border: `${theme.borderWidth}px solid ${theme.primary}`, borderRadius: theme.radius, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                  <Lock className="w-4 h-4"/> Image locked by seller — cannot be changed.
                </div>
              ) : (
                <>
                  {(!selectedDesign.image_url || selected.allow_replace !== false) && (
                    <label style={{ ...btnPrimary, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer", fontSize: 14 }}>
                      {uploading ? <><Loader2 className="w-4 h-4 animate-spin"/> {tpl.category === "face_stamp" ? "Sketching…" : "Uploading…"}</> : <><Upload className="w-4 h-4"/> {selectedDesign.image_url ? "Replace image" : (tpl.category === "face_stamp" ? "Upload photo" : "Upload image")}</>}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(selected.id, e)} data-testid="upload-image-input"/>
                    </label>
                  )}
                  <div style={{ fontSize: 11, opacity: 0.7 }}>
                    {tpl.category === "face_stamp"
                      ? <>Pick a filter below to preview instantly — nothing is sent to any AI service. Min {selected.min_image_dimension || 300}px per side · Max 50MB</>
                      : <>Min {selected.min_image_dimension || 300}px per side · Max 50MB</>}
                  </div>
                  {tpl.category === "face_stamp" && rawPhotoFiles.current[selected.id] && (
                    <div data-testid="face-filter-block">
                      <label style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", display: "block", marginBottom: 4 }}>Photo filter</label>
                      <select
                        style={inputStyle}
                        value={selectedDesign.face_filter || "no_effect"}
                        onChange={(e) => applyFaceFilter(selected.id, e.target.value, rawPhotoFiles.current[selected.id])}
                        data-testid="face-filter-select"
                      >
                        {allowedFaceFilters.map((f) => (
                          <option key={f.key} value={f.key}>{f.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {selectedDesign.image_url && selected.allow_zoom !== false && (
                    <div data-testid="image-zoom-block">
                      <label style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", display: "block", marginBottom: 4 }}>Zoom · {(selectedDesign.zoom || 1).toFixed(2)}×</label>
                      <input type="range" min={0.5} max={4} step={0.05}
                        value={selectedDesign.zoom || 1}
                        onChange={(e) => setZone(selected.id, { zoom: +e.target.value })}
                        className="w-full" data-testid="image-zoom-slider"/>
                    </div>
                  )}
                  {selectedDesign.image_url && selected.allow_move !== false && (
                    <div style={{ fontSize: 11, opacity: 0.7 }} data-testid="image-move-hint">Drag the image on the canvas to reposition — clipping keeps it inside the printable area.</div>
                  )}
                  {selectedDesign.image_url && selected.allow_reset !== false && (
                    <button
                      onClick={() => setZone(selected.id, { image_url: selected.default_image_url || "", zoom: 1, offsetX: 0, offsetY: 0 })}
                      style={{ ...btnGhost, fontSize: 12, padding: "6px 10px" }}
                      data-testid="reset-image-btn"
                    >Reset image</button>
                  )}
                </>
              )}
            </div>
          )}

          {!selected && (
            <div style={{ ...cardStyle, padding: 16, fontSize: 13, opacity: 0.75 }}>Pick a layer above to start editing.</div>
          )}
        </div>
      </div>

      {checkout && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => setCheckout(false)}>
          <div style={{ ...cardStyle, padding: 24, width: "100%", maxWidth: 440 }} onClick={(e) => e.stopPropagation()} data-testid="checkout-modal">
            <h2 style={{ fontSize: 24, fontWeight: 900, margin: 0, marginBottom: 16 }}>Your details</h2>
            <div className="space-y-3">
              <input placeholder="Full name*" style={inputStyle} value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} data-testid="customer-name"/>
              <input placeholder="Email*" type="email" style={inputStyle} value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} data-testid="customer-email"/>
              <input placeholder="Phone (optional)" style={inputStyle} value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} data-testid="customer-phone"/>
              <div style={{ padding: 12, background: "#dcfce7", border: `${theme.borderWidth}px solid #15803d`, borderRadius: theme.radius }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#14532d", marginBottom: 6 }}>
                  📱 Share WhatsApp number for final print design confirmation
                </div>
                <input placeholder="+91 98765 43210" style={{ ...inputStyle, borderColor: "#15803d" }} value={customer.whatsapp} onChange={(e) => setCustomer({ ...customer, whatsapp: e.target.value })} data-testid="customer-whatsapp"/>
                <div style={{ fontSize: 11, color: "#166534", marginTop: 4 }}>Seller will share the final image on WhatsApp for your approval before printing.</div>
              </div>
              <textarea placeholder="Notes / special instructions" rows={3} style={{ ...inputStyle, resize: "none" }} value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} data-testid="customer-notes"/>
            </div>
            <div style={{ marginTop: 20, display: "flex", gap: 8 }}>
              <button onClick={() => setCheckout(false)} style={{ ...btnGhost, flex: 1 }}>Cancel</button>
              <button onClick={submit} disabled={submitting || hasOverflow} style={{ ...btnPrimary, flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 4, opacity: (submitting || hasOverflow) ? 0.5 : 1, cursor: (submitting || hasOverflow) ? "not-allowed" : "pointer" }} data-testid="checkout-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <ShoppingCart className="w-4 h-4"/>}
                Add to cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
